import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {useNotebookItemCollapse} from 'notebook/tables/collapse/useNotebookItemCollapse';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {FONT_PROXIMA_TITLE, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Badge, {BadgeColor} from 'ui-library/Badge';
import CollapserChevron from 'ui-library/CollapserChevron';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

interface PlateContainerProps {
  /** Text to be displayed in the header. */
  header: string;
  /** Controls to be displayed on the right side of the header, e.g. buttons. */
  headerControls: ReactNode;
  /** Callback for when the header is right-clicked. */
  onHeaderContextMenu: (e: ElementMouseEvent) => void;
  /** Subtext to be displayed in the header. */
  subheader: string;
  /** Type of Plate Map RECORD | DESIGN */
  plateMapType?: PlateMapType;
  /** Body of the container. */
  children?: ReactNode;
  /** Used to determine collapse state on the plate. */
  collapseId?: string;
}

/**
 * A notebook-compatible "card" container, shared between PlateMap-rendering plugins
 * (i.e. plate designs and plate records).
 */
export default function PlateMapContainer(props: PlateContainerProps): JSX.Element {
  const {isCollapsed, onToggleCollapsed} = useNotebookItemCollapse(props.collapseId);

  // We use data-mediocre-item-select to only select the component when the header is clicked.
  // This overrides the default behavior of clicking anywhere that comes from SelectableNotePart.
  // TODO(BNCH-75467): Remove the duplicated styles which are used in temporary-aphrodite-styles.less
  return (
    <div
      data-mediocre-item-select={0}
      className={classNames(css(styles.root), 'notebook-export--plate-record')}
    >
      <div className={classNames(css(styles.headerWrapper), 'notebook-export--plate-record-header-wrapper')}>
        <div
          data-mediocre-item-select={1}
          className={classNames(css(styles.header), 'notebook-export--plate-record-header u-pointer')}
        >
          <div className={classNames(css(styles.headerTitle))} onContextMenu={props.onHeaderContextMenu}>
            <BenchlingIcon
              className={classNames(css(styles.headerIcon), 'notebook-export--plate-record-header-icon')}
              icon="plate"
            />
            <span className={classNames(css(styles.title), 'notebook-export--plate-record-title')}>
              {props.header}
            </span>
            <span
              {...getTestProps({
                component: 'PlateMapContainer',
                key: 'name',
              })}
              className={classNames(css(styles.name), 'notebook-export--plate-record-name')}
            >
              {props.subheader}
            </span>
            {props.plateMapType && (
              <Badge
                color={props.plateMapType === PlateMapType.RECORD ? BadgeColor.BLUE : BadgeColor.GRAY}
                bold={true}
              >
                {props.plateMapType === PlateMapType.RECORD ? 'RECORD' : 'DESIGN'}
              </Badge>
            )}
          </div>
          {(props.collapseId == null || !isCollapsed) && props.headerControls}
        </div>
        {props.collapseId != null && (
          <div
            className={classNames(
              css(styles.collapserChevronWrapper),
              'notebook-export--collapser-chevron-wrapper u-tiny-spacing-left'
            )}
          >
            <CollapserChevron
              isExpanded={!isCollapsed}
              onToggleCollapsed={onToggleCollapsed}
              name="plate map"
            />
          </div>
        )}
      </div>
      {(props.collapseId == null || !isCollapsed) && (
        <div className={css(styles.contents)}>{props.children}</div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  contents: {
    padding: 16,
    display: 'flex',
    height: 625,
  },
  root: {
    borderRadius: 4,
    border: `1px solid ${Color.GRAY_300}`,
    margin: '8px 0',
    backgroundColor: Color.WHITE,
  },
  headerWrapper: {
    display: 'flex',
  },
  header: {
    padding: 8,
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${Color.GRAY_300}`,
    flex: '0 0 100%',
  },
  headerIcon: {
    verticalAlign: 'middle',
  },
  headerTitle: {
    flex: 1,
  },
  collapserChevronWrapper: {
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    ...FONT_PROXIMA_TITLE,
    marginLeft: 8,
    verticalAlign: 'middle',
  },
  name: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
    marginLeft: 8,
    marginRight: 8,
    verticalAlign: 'middle',
  },
});
